import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line
} from "recharts";
import { useDashboardData } from "../api/useDashboardData";
import "../Styles/Dashboard.css";

const Dashboard = () => {
  const { summary, trend, topCustomers, topProducts, mom, fy, lifetime, loading, error } =
    useDashboardData();

  // Get the current company from localStorage to use as a dependency.
  const company = localStorage.getItem("company") || "SUNORA";

  const [chartColors, setChartColors] = useState({
    sales: '#007bff',
    profit: '#28a745',
  });

  useEffect(() => {
    // This effect now re-runs whenever the `company` changes.
    // MODIFIED: It now reads the style from document.body, which is where
    // your Layout component applies the theme class.
    const bodyStyles = getComputedStyle(document.body);
    const salesColorVar = bodyStyles.getPropertyValue('--theme-color').trim();
    
    // We check if a valid color was found before setting the state.
    if (salesColorVar) {
        setChartColors({
          sales: salesColorVar,
          profit: '#28a745', 
        });
    }
  }, [company]); // Dependency ensures this runs on theme change.


  if (loading) return <div className="message-container">Loading dashboard...</div>;
  if (error) return <div className="message-container error">{error}</div>;

  const formatCurrency = (v) =>
    `₹${parseFloat(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const KpiBlock = ({ title, data, isProfit = false }) => {
    const delta =
      data?.mom?.pct_change == null
        ? "—"
        : `${data.mom.pct_change > 0 ? "▲" : data.mom.pct_change < 0 ? "▼" : ""} ${
            data.mom.pct_change
          }%`;
    
    return (
      <div className="analytics-card">
        <h2>{title}</h2>
        <div className="kpi-row">
          <div className="kpi">
            <span>Current Month</span>
            <strong>{formatCurrency(data?.mom?.current || 0)}</strong>
          </div>
          <div className="kpi">
            <span>Previous Month</span>
            <strong>{formatCurrency(data?.mom?.previous || 0)}</strong>
          </div>
          <div className={`kpi ${data?.mom?.pct_change > 0 ? "up" : data?.mom?.pct_change < 0 ? "down" : ""}`}>
            <span>Change</span>
            <strong>{delta}</strong>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data?.series || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => formatCurrency(v)} />
            <Line type="monotone" dataKey="total" stroke={isProfit ? chartColors.profit : chartColors.sales} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    
      <div className="dashboard-grid">
        {/* Summary Cards */}
        <div className="summary-card sales">
          <h4>Sales (This Month)</h4>
          <p>{formatCurrency(summary?.total_sales)}</p>
        </div>
        <div className="summary-card profit">
          <h4>Profit (This Month)</h4>
          <p>{formatCurrency(summary?.total_profit)}</p>
        </div>
        <div className="summary-card invoices">
          <h4>Invoices (This Month)</h4>
          <p>{summary?.total_invoices}</p>
        </div>
        <div className="summary-card outstanding">
          <h4>Total Outstanding</h4>
          <p>{formatCurrency(summary?.total_outstanding)}</p>
        </div>
        <div className="summary-card customers">
          <h4>Total Customers</h4>
          <p>{summary?.total_customers}</p>
        </div>

        {/* Daily Sales */}
        <div className="chart-container">
          <h2>Daily Sales (This Month)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={(value) => `₹${Math.round(value / 1000)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="total" fill={chartColors.sales} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Lists */}
        <div className="list-container">
          <h2>Top Customers (This Month)</h2>
          <ol>
            {topCustomers.map((c, i) => (
              <li key={i}>
                {c.customer}
                <span>{formatCurrency(c.total_spent)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="list-container">
          <h2>Top Products (This Month)</h2>
          <ol>
            {topProducts.map((p, i) => (
              <li key={i}>
                {p.product}
                <span>Qty: {p.total_qty}</span>
              </li>
            ))}
          </ol>
        </div>
        
        {/* Dual Month-over-Month Charts */}
        <div className="dual-chart-container">
            <KpiBlock title="Month-over-Month Sales" data={mom.sales} />
            <KpiBlock title="Month-over-Month Profit" data={mom.profit} isProfit={true} />
        </div>

        {/* Dual Financial Year Charts */}
        <div className="dual-chart-container">
            <div className="analytics-card">
                <h2>Financial Year Sales</h2>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={fy}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="finyear" />
                        <YAxis tickFormatter={(v) => `₹${Math.round(v / 100000) / 10}L`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="sales_total" fill={chartColors.sales} name="Sales" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="analytics-card">
                <h2>Financial Year Profit</h2>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={fy}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="finyear" />
                        <YAxis tickFormatter={(v) => `₹${Math.round(v / 100000) / 10}L`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="profit_total" fill={chartColors.profit} name="Profit" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* Lifetime Stats */}
        <div className="analytics-card full-width">
          <h2>Lifetime Stats</h2>
          <div className="kpi-grid">
            <div className="kpi">
              <span>Total Revenue</span>
              <strong>{formatCurrency(lifetime?.total_revenue || 0)}</strong>
            </div>
            <div className="kpi">
              <span>Invoices</span>
              <strong>{lifetime?.invoice_count || 0}</strong>
            </div>
            <div className="kpi">
              <span>Avg. Invoice Value</span>
              <strong>{formatCurrency(lifetime?.avg_invoice || 0)}</strong>
            </div>
            <div className="kpi">
              <span>First Invoice</span>
              <strong>{lifetime?.first_invoice_date ? new Date(lifetime.first_invoice_date).toLocaleDateString("en-GB") : "—"}</strong>
            </div>
            <div className="kpi">
              <span>Last Invoice</span>
              <strong>{lifetime?.last_invoice_date ? new Date(lifetime.last_invoice_date).toLocaleDateString("en-GB") : "—"}</strong>
            </div>
            <div className="kpi">
              <span>Largest Invoice</span>
              <strong>
                {lifetime?.largest_invoice
                  ? `${formatCurrency(lifetime.largest_invoice.amount)} (#${lifetime.largest_invoice.docno})`
                  : "—"}
              </strong>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Dashboard;
